import { useLayoutEffect, useState } from 'react';

const PAGE_HEADER_SELECTOR = '#page-header';

/**
 * Extra pixels below the measured header (matches PageContent `paddingTop: 3` at default MUI spacing).
 */
const DEFAULT_EXTRA_TOP_OFFSET_PX = 48;

/**
 * Used before the header is measured (SSR, missing node, or first tick).
 */
const FALLBACK_TOTAL_OFFSET_PX = 154;

type UseTranscriptionPanelViewportMaxHeightOptions = {
  /** Pixels added on top of the measured `.page-header` height (default: 24). */
  extraTopOffsetPx?: number;
};

/**
 * Returns a CSS `max-height` value (`calc(100dvh - Npx)`) so the transcript panel stays within
 * the viewport below the page header. Observes the header with ResizeObserver when tabs or
 * layout change its height.
 */
export function useTranscriptionPanelViewportMaxHeight(
  options: UseTranscriptionPanelViewportMaxHeightOptions = {},
) {
  const extraTopOffsetPx = options.extraTopOffsetPx ?? DEFAULT_EXTRA_TOP_OFFSET_PX;
  const [totalOffsetPx, setTotalOffsetPx] = useState(FALLBACK_TOTAL_OFFSET_PX);

  useLayoutEffect(() => {
    const el = document.querySelector(PAGE_HEADER_SELECTOR);
    if (!el || !(el instanceof HTMLElement)) {
      return;
    }

    const measure = () => {
      const heightPx = Math.ceil(el.getBoundingClientRect().height);
      setTotalOffsetPx(heightPx + extraTopOffsetPx);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => ro.disconnect();
  }, [extraTopOffsetPx]);

  return `calc(100dvh - ${totalOffsetPx}px)`;
}
